import { useEffect, useMemo, useRef, useState } from 'react'

import { cn } from '../../utils/cn'

export type MultiSelectOption = {
  value: number
  label: string
}

type Props = {
  label: string
  options: MultiSelectOption[]
  selected: number[]
  /** 'only' は選んだものだけ、'except' は選んだものを除く。 */
  mode: 'only' | 'except'
  /** 「該当なし」を選ぶための擬似項目。選ぶと selected は空になる。 */
  noneLabel: string
  noneSelected: boolean
  searchPlaceholder: string
  onlyLabel: string
  exceptLabel: string
  selectedSummary: (count: number) => string
  allLabel: string
  onChange: (next: {
    selected: number[]
    mode: 'only' | 'except'
    noneSelected: boolean
  }) => void
}

/**
 * 複数選択できる絞り込み。`details` を使うので開閉のキーボード操作は
 * ブラウザ標準の挙動に任せられる。
 */
export function ManagementMultiSelectFilter({
  label,
  options,
  selected,
  mode,
  noneLabel,
  noneSelected,
  searchPlaceholder,
  onlyLabel,
  exceptLabel,
  selectedSummary,
  allLabel,
  onChange,
}: Props) {
  const [keyword, setKeyword] = useState('')
  const detailsRef = useRef<HTMLDetailsElement>(null)

  // 外側をクリックしたら閉じる。開きっぱなしだと下の一覧や他のフィルタに
  // 重なり続け、複数のフィルタを同時に開いたままにもできてしまう。
  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      const element = detailsRef.current
      if (!element?.open) return
      if (event.target instanceof Node && element.contains(event.target)) return
      element.open = false
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      const element = detailsRef.current
      if (element?.open) element.open = false
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [])

  const visible = useMemo(() => {
    const normalized = keyword.trim().toLowerCase()
    if (!normalized) return options
    return options.filter((option) =>
      option.label.toLowerCase().includes(normalized),
    )
  }, [options, keyword])

  const summary = noneSelected
    ? noneLabel
    : selected.length > 0
      ? selectedSummary(selected.length)
      : allLabel

  const toggle = (value: number) => {
    const next = selected.includes(value)
      ? selected.filter((item) => item !== value)
      : [...selected, value]
    onChange({ selected: next, mode, noneSelected: false })
  }

  return (
    <details ref={detailsRef} className="group relative min-w-[180px]">
      <summary className="grid cursor-pointer list-none gap-2 text-xs font-semibold text-ink [&::-webkit-details-marker]:hidden">
        {label}
        <span
          className={cn(
            'flex h-11 items-center justify-between rounded-2xl border bg-white/80 px-3 text-sm shadow-sm',
            noneSelected || selected.length > 0
              ? 'border-accent text-ink'
              : 'border-border/80 text-muted',
          )}
        >
          {summary}
          <span aria-hidden="true">▾</span>
        </span>
      </summary>

      <div className="absolute z-20 mt-2 w-[280px] rounded-2xl border border-border/80 bg-white p-3 shadow-lg">
        <input
          type="search"
          className="h-9 w-full rounded-xl border border-border/80 px-2 text-sm"
          placeholder={searchPlaceholder}
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
        />

        <div
          className="mt-2 inline-flex overflow-hidden rounded-lg border border-border/80 text-xs"
          role="group"
          aria-label={`${label} ${onlyLabel} / ${exceptLabel}`}
        >
          {(['only', 'except'] as const).map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={mode === value}
              className={cn(
                'px-3 py-1',
                mode === value ? 'bg-accent text-white' : 'text-ink',
              )}
              onClick={() => onChange({ selected, mode: value, noneSelected })}
            >
              {value === 'only' ? onlyLabel : exceptLabel}
            </button>
          ))}
        </div>

        <ul className="mt-2 max-h-56 overflow-y-auto text-sm">
          {visible.map((option) => (
            <li key={option.value}>
              <label className="flex cursor-pointer items-center gap-2 py-1">
                <input
                  type="checkbox"
                  checked={selected.includes(option.value)}
                  onChange={() => toggle(option.value)}
                />
                {option.label}
              </label>
            </li>
          ))}
          <li className="mt-1 border-t border-border/60 pt-1">
            <label className="flex cursor-pointer items-center gap-2 py-1">
              <input
                type="checkbox"
                checked={noneSelected}
                onChange={() =>
                  onChange({
                    // 「該当なし」と個別選択は同時に成立しない
                    selected: [],
                    mode,
                    noneSelected: !noneSelected,
                  })
                }
              />
              {noneLabel}
            </label>
          </li>
        </ul>
      </div>
    </details>
  )
}
